/**
 * useOfflineReady — central hook for offline state & sync awareness.
 *
 * Provides:
 *  - isOnline: current connectivity state
 *  - syncStatus: 'idle' | 'syncing' | 'done' | 'error'
 *  - pendingCount: number of queued offline writes
 *  - lastSyncResult: result of the most recent flush attempt
 *
 * Automatically triggers a sync flush when connectivity is restored.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { flushSyncQueue, flushPendingPhotos, type SyncStatus, type SyncResult } from '@/lib/syncQueue';
import { getPendingSyncCount, getPendingPhotoCount } from '@/lib/offlineStore';

export interface OfflineState {
  isOnline: boolean;
  syncStatus: SyncStatus;
  pendingCount: number;
  lastSyncResult: SyncResult | null;
  /** Manually trigger a sync flush */
  flushNow: () => Promise<void>;
}

export function useOfflineReady(): OfflineState {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const flushingRef = useRef(false);

  // Track online/offline events
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Refresh pending count periodically and on connectivity change
  useEffect(() => {
    if (!user) return;
    const refresh = async () => {
      const [syncCount, photoCount] = await Promise.all([
        getPendingSyncCount(user.id),
        getPendingPhotoCount(user.id),
      ]);
      setPendingCount(syncCount + photoCount);
    };
    refresh();
    // Poll every 5s so the UI stays reasonably fresh
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [user, isOnline, syncStatus]);

  const flushNow = useCallback(async () => {
    if (!user || flushingRef.current) return;
    flushingRef.current = true;
    try {
      const result = await flushSyncQueue(user.id, setSyncStatus);
      setLastSyncResult(result);

      // Flush queued photo uploads after data sync
      await flushPendingPhotos(user.id);

      const [syncCount, photoCount] = await Promise.all([
        getPendingSyncCount(user.id),
        getPendingPhotoCount(user.id),
      ]);
      setPendingCount(syncCount + photoCount);
    } finally {
      flushingRef.current = false;
    }
  }, [user]);

  // Auto-flush when we come back online
  useEffect(() => {
    if (isOnline && user && pendingCount > 0) {
      flushNow();
    }
  }, [isOnline, user, pendingCount, flushNow]);

  return { isOnline, syncStatus, pendingCount, lastSyncResult, flushNow };
}
